/* eslint-disable react/jsx-wrap-multilines */
import { FC } from 'react';
import { setModal } from 'modules/_shared/redux/modalActions';
import { useTranslate } from 'hooks/useTranslate';
import { useDispatch, useSelector } from 'react-redux';

import sizes from 'constants/sizes';
import { State } from 'redux/initialState';

import { Document, HoldingClass, Share, SharesTitle } from 'types';

import isSuperAdmin from 'utils/isSuperAdmin';
import { formatCurrencyDecimals, formatNumber } from 'utils/formats';
import { societyHasTitles } from 'utils/societyHasTitles';

import UpdateShares from 'modules/_shared/components/Modals/UpdateShares';
import DocumentsColumn from 'modules/_shared/components/DocumentsColumn';
import { getShareEncumbranceNames } from 'modules/partners/utils/getShareEncumbranceNames';

import {
  generateSharesTitlesFilename,
  generateSharesTitlesFilenameWithCoOwners,
} from 'modules/partners/utils/generateSharesTitlesFilename';
import { useGetShareTitle } from '../hooks/useGetShareTitle';
import EncumbrancesTooltip from './EncumbrancesTooltip';
import {
  ShareTitleTooltipText,
  ShareTitleTooltipTextWithCoOwners,
} from '../../ShareTitleTooltip';

type PartnerSharesTableRowProps = {
  fetchPartnerData: () => void;
  index: number;
  isCoOwner: boolean;
  partnerId: string;
  share: Share;
  sharesTitles: SharesTitle[];
  showEncumbranceColumn: boolean;
  showSharesTitleColumn: boolean;
  hasMainSharesTitleDisabled: boolean;
  societySharesTitleDocument?: Document;
  societySharesTitleDocumentWithCoOwners?: Document;
};

const PartnerSharesTableRow: FC<PartnerSharesTableRowProps> = ({
  fetchPartnerData,
  index,
  isCoOwner,
  partnerId,
  share,
  sharesTitles,
  showEncumbranceColumn,
  showSharesTitleColumn,
  hasMainSharesTitleDisabled,
  societySharesTitleDocument,
  societySharesTitleDocumentWithCoOwners,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslate();

  const user = useSelector((state: State) => state?.user);
  const isAdmin = useSelector((state: State) => state.society?.role?.isAdmin);
  const actualSociety = useSelector(
    (state: State) => state?.society?.actualSociety || {}
  );

  const hasTitlesEnabled = societyHasTitles(actualSociety);
  const hasTitlesWithCoOwnersEnabled =
    !!societySharesTitleDocumentWithCoOwners &&
    !!share?.encumbrances?.length &&
    hasTitlesEnabled;

  const { sharesTitle, titleWatermarkText } = useGetShareTitle({
    share,
    sharesTitles,
  }).data;

  const sharesCount = share?.to - share?.from + 1;
  const shareClass = share?.shareClass as HoldingClass;
  const shareClassName = shareClass?.name || '';
  const nominalValue =
    shareClass?.nominalValue || actualSociety?.nominalValue || 0;

  const downloadTitleName = sharesTitle
    ? generateSharesTitlesFilename(sharesTitle, t)
    : '';

  const downloadTitleWithCoOwnersName = sharesTitle
    ? generateSharesTitlesFilenameWithCoOwners(sharesTitle, t)
    : '';

  const encumbranceNames = getShareEncumbranceNames(share, partnerId, t);

  const societyShareTitleConfiguration =
    actualSociety?.configuration?.sharesTitles;

  const handleClickRow = () => {
    if (isAdmin || isSuperAdmin(user)) {
      dispatch(
        setModal(
          <UpdateShares
            share={share}
            society={actualSociety}
            isReadOnly={isCoOwner}
            fetchPartnerData={fetchPartnerData}
            size={sizes.XL}
          />
        )
      );
    }
  };

  return (
    <>
      <tr className="tb-tnx-item cursor-pointer">
        <td
          className="text-left vertical-align-middle"
          onClick={handleClickRow}
        >
          <b>{index}</b>
        </td>

        <td
          className="text-right vertical-align-middle"
          onClick={handleClickRow}
        >
          {sharesCount ? formatNumber(sharesCount) : '-'}
        </td>

        {!isCoOwner && (
          <td
            className="text-right vertical-align-middle"
            onClick={handleClickRow}
          >
            {formatCurrencyDecimals(nominalValue)}
          </td>
        )}

        {isCoOwner && (
          <td className="vertical-align-middle" onClick={handleClickRow}>
            {encumbranceNames || '-'}
          </td>
        )}

        <td
          className="text-left text-ellipsis vertical-align-middle"
          onClick={handleClickRow}
        >
          {shareClassName}
        </td>

        <td
          className="text-right vertical-align-middle"
          onClick={handleClickRow}
        >
          {share?.from ? formatNumber(share.from) : '-'}
        </td>

        <td
          className="text-right vertical-align-middle"
          onClick={handleClickRow}
        >
          {share?.to ? formatNumber(share.to) : '-'}
        </td>

        {showEncumbranceColumn && (
          <td
            className="text-center vertical-align-middle"
            onClick={handleClickRow}
          >
            <EncumbrancesTooltip
              shareId={share?._id}
              encumbrances={share?.encumbrances || []}
            />
          </td>
        )}

        {showSharesTitleColumn && (
          <>
            {hasTitlesEnabled && sharesTitle ? (
              <div className="d-flex justify-content-center align-items-center">
                {!hasMainSharesTitleDisabled && (
                  <DocumentsColumn
                    index={index}
                    documentsCount={1}
                    documents={
                      societySharesTitleDocument
                        ? [societySharesTitleDocument]
                        : []
                    }
                    downloadName={downloadTitleName}
                    hideOnMobile={false}
                    isLandscape={
                      societyShareTitleConfiguration?.isLandscape || false
                    }
                    sources={{
                      society: actualSociety?._id,
                      sharesTitle: sharesTitle?._id,
                    }}
                    tableView={false}
                    templateWatermarkText={titleWatermarkText}
                    templateBackgroundImage={
                      societyShareTitleConfiguration?.backgroundImage || ''
                    }
                    tooltipId={`share-title-tooltip-${index}`}
                    templateTextPadding={
                      societyShareTitleConfiguration?.textPadding || {}
                    }
                    tooltipText={
                      <ShareTitleTooltipText sharesTitle={sharesTitle} />
                    }
                  />
                )}

                {hasTitlesWithCoOwnersEnabled && (
                  <DocumentsColumn
                    index={index}
                    documentsCount={1}
                    documents={
                      societySharesTitleDocumentWithCoOwners
                        ? [societySharesTitleDocumentWithCoOwners]
                        : []
                    }
                    downloadName={downloadTitleWithCoOwnersName}
                    hideOnMobile={false}
                    isLandscape={
                      actualSociety?.configuration?.sharesTitles?.isLandscape ||
                      false
                    }
                    sources={{
                      society: actualSociety?._id,
                      sharesTitle: sharesTitle?._id,
                    }}
                    tableView={false}
                    templateWatermarkText={titleWatermarkText}
                    tooltipId={`share-title-with-coowners-tooltip-${index}`}
                    tooltipText={
                      <ShareTitleTooltipTextWithCoOwners
                        sharesTitle={sharesTitle}
                      />
                    }
                  />
                )}
              </div>
            ) : (
              <td className="text-center vertical-align-middle">
                <span>-</span>
              </td>
            )}
          </>
        )}
      </tr>
    </>
  );
};
export default PartnerSharesTableRow;
